/**
 * Negocio › Por aprobar — un solo lugar para lo que espera una acción de Structura:
 *  - Altas pagadas por transferencia: se acreditan y la cuenta se crea en un paso.
 *  - Pruebas gratis pendientes: se aprueban o rechazan acá. La cuenta, los flags y los mails los crea saas-backend
 *    (/v1/admin/trials), al que este admin llama servidor a servidor.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useAppDialogs } from "@/components/ui/app-dialogs";
import { CheckCircle2, Gift, Landmark, Loader2, Mail, MessageCircle, XCircle } from "lucide-react";
import { buildLeadWhatsAppUrl } from "@/lib/lead-whatsapp";
import type { OnboardingSession } from "@/lib/self-service-provisioning";
import { clientRequestHeaders, ensureCsrfToken } from "@/lib/sm-api";

const BILLING_LABEL: Record<string, string> = {
  monthly_oneshot: "Mensual",
  annual_oneshot: "12 meses",
  m24_oneshot: "24 meses",
  m48_oneshot: "48 meses",
};

function fmtMoney(cents: number | null | undefined, currency = "ARS") {
  if (cents == null) return "—";
  return (cents / 100).toLocaleString("es-AR", { style: "currency", currency, maximumFractionDigits: 0 });
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleString("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function Contact({ s }: { s: OnboardingSession }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {s.ownerPhone ? (
        <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" asChild>
          <a
            href={buildLeadWhatsAppUrl({
              nombre: s.ownerName ?? "",
              negocio: s.businessName ?? "",
              email: s.ownerEmail ?? "",
              telefono: s.ownerPhone,
              planInteresado: s.planId,
            })}
            target="_blank"
            rel="noreferrer"
          >
            <MessageCircle className="h-3 w-3" /> WhatsApp
          </a>
        </Button>
      ) : null}
      {s.ownerEmail ? (
        <Button size="sm" variant="outline" className="h-7 gap-1 text-xs" asChild>
          <a href={`mailto:${s.ownerEmail}`}>
            <Mail className="h-3 w-3" /> Email
          </a>
        </Button>
      ) : null}
    </div>
  );
}

export function AdminApprovalsPanel() {
  const { confirm, prompt } = useAppDialogs();
  const [transfers, setTransfers] = useState<OnboardingSession[]>([]);
  const [trials, setTrials] = useState<OnboardingSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    fetch("/api/admin/onboarding?view=aprobaciones", { credentials: "include" })
      .then(async (res) => {
        const data = (await res.json()) as { transfers?: OnboardingSession[]; trials?: OnboardingSession[]; error?: string };
        if (!res.ok) throw new Error(data.error ?? "Error");
        setTransfers(data.transfers ?? []);
        setTrials(data.trials ?? []);
      })
      .catch((e) => toast.error(e instanceof Error ? e.message : "No se pudo cargar lo pendiente"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const post = async (sessionId: string, action: "confirm_and_provision" | "expire" | "approve_trial" | "reject_trial", reason?: string) => {
    await ensureCsrfToken();
    const res = await fetch("/api/admin/onboarding", {
      method: "POST",
      headers: { ...clientRequestHeaders(), "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ sessionId, action, reason }),
    });
    const data = (await res.json()) as { error?: string; tenantId?: string };
    if (!res.ok) throw new Error(data.error ?? "Error");
    return data;
  };

  const credit = async (s: OnboardingSession) => {
    const ok = await confirm({
      title: `¿Acreditar la transferencia de ${s.businessName ?? "este alta"}?`,
      description: `Confirmás que recibiste ${fmtMoney(s.amountCents, s.currency)} con la referencia ${s.id}. Se crea la cuenta y le llega el acceso a ${s.ownerEmail ?? "la persona"}.`,
      confirmLabel: "Acreditar y crear cuenta",
    });
    if (!ok) return;
    setActing(s.id);
    try {
      await post(s.id, "confirm_and_provision");
      toast.success(`Cuenta de ${s.businessName ?? "la tienda"} creada`);
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo acreditar");
    } finally {
      setActing(null);
    }
  };

  const discard = async (s: OnboardingSession) => {
    const ok = await confirm({
      title: `¿Descartar el alta de ${s.businessName ?? "esta sesión"}?`,
      description: "Usalo si la transferencia nunca llegó o era una prueba. La sesión queda vencida y sale de esta lista.",
      confirmLabel: "Descartar",
      destructive: true,
    });
    if (!ok) return;
    setActing(s.id);
    try {
      await post(s.id, "expire");
      toast.success("Alta descartada");
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo descartar");
    } finally {
      setActing(null);
    }
  };

  const approveTrial = async (s: OnboardingSession) => {
    const ok = await confirm({
      title: `¿Aprobar la prueba de ${s.businessName ?? "este negocio"}?`,
      description: `Se crea la cuenta y le llega a ${s.ownerEmail ?? "la persona"} el mail de bienvenida con el acceso a su panel.`,
      confirmLabel: "Aprobar y crear cuenta",
    });
    if (!ok) return;
    setActing(s.id);
    try {
      await post(s.id, "approve_trial");
      toast.success(`Prueba de ${s.businessName ?? "la tienda"} aprobada`);
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo aprobar");
    } finally {
      setActing(null);
    }
  };

  const rejectTrial = async (s: OnboardingSession) => {
    const reason = await prompt({
      title: `¿Rechazar la prueba de ${s.businessName ?? "este negocio"}?`,
      description: "Le mandamos un mail cordial ofreciendo una demo o un plan. El motivo queda solo para el registro interno.",
      placeholder: "Motivo (opcional)",
      confirmLabel: "Rechazar",
      allowEmpty: true,
    });
    if (reason === null) return;
    setActing(s.id);
    try {
      await post(s.id, "reject_trial", reason);
      toast.success("Prueba rechazada");
      load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "No se pudo rechazar");
    } finally {
      setActing(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <Landmark className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold">Altas por transferencia</h3>
          <Badge variant={transfers.length ? "default" : "secondary"}>{transfers.length}</Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          Cuando veas la plata en la cuenta, acreditala: se crea la cuenta y le llega el acceso por email. La referencia es la que
          figura en el mail que recibió la persona.
        </p>
        {transfers.length === 0 ? (
          <p className="rounded-lg border border-dashed py-6 text-center text-sm text-muted-foreground">No hay transferencias esperando.</p>
        ) : (
          <div className="space-y-2">
            {transfers.map((s) => (
              <div key={s.id} className="space-y-3 rounded-lg border bg-card p-4">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 space-y-0.5">
                    <p className="truncate text-sm font-semibold">{s.businessName ?? "Sin nombre"}</p>
                    <p className="text-xs text-muted-foreground">
                      {s.ownerName ?? "—"} · {s.ownerEmail ?? "sin email"}
                      {s.ownerPhone ? ` · ${s.ownerPhone}` : ""}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Plan <strong className="text-foreground">{s.planId}</strong> · {BILLING_LABEL[s.billingMode] ?? s.billingMode} · Referencia{" "}
                      <code className="text-[11px]">{s.id}</code>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold">{fmtMoney(s.amountCents, s.currency)}</p>
                    <p className="text-[11px] text-muted-foreground">{fmtDate(s.createdAt)}</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Contact s={s} />
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="ghost" className="h-8 gap-1 text-xs" disabled={acting === s.id} onClick={() => void discard(s)}>
                      <XCircle className="h-3.5 w-3.5" /> Descartar
                    </Button>
                    <Button size="sm" className="h-8 gap-1 text-xs" disabled={acting === s.id} onClick={() => void credit(s)}>
                      {acting === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                      Acreditar y crear cuenta
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Gift className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold">Pruebas gratis por aprobar</h3>
            <Badge variant={trials.length ? "default" : "secondary"}>{trials.length}</Badge>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Al aprobar se crea la cuenta con la prueba y le llega el acceso por email. Al rechazar le llega un mail cordial ofreciendo
          una demo o un plan.
        </p>
        {trials.length === 0 ? (
          <p className="rounded-lg border border-dashed py-6 text-center text-sm text-muted-foreground">No hay pruebas esperando aprobación.</p>
        ) : (
          <div className="space-y-2">
            {trials.map((s) => (
              <div key={s.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-card p-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{s.businessName ?? "Sin nombre"}</p>
                  <p className="text-xs text-muted-foreground">
                    {s.ownerName ?? "—"} · {s.ownerEmail ?? "sin email"} · {s.businessProfile} · {fmtDate(s.createdAt)}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <Contact s={s} />
                  <Button size="sm" variant="ghost" className="h-8 gap-1 text-xs" disabled={acting === s.id} onClick={() => void rejectTrial(s)}>
                    <XCircle className="h-3.5 w-3.5" /> Rechazar
                  </Button>
                  <Button size="sm" className="h-8 gap-1 text-xs" disabled={acting === s.id} onClick={() => void approveTrial(s)}>
                    {acting === s.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                    Aprobar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
